import React from 'react';
import { Box, Button, Divider, Grid, Typography } from '@mui/material';
import { DocumentScanner } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { apiConstants, getGeneric } from '../../common/constants';
import { formatDate, parseDateAndTime } from '../../helpers/UtilityHelper';
import { categoryLabel } from './ExtraDocsEditor';

/**
 * View-only documents of a PTL shipment, same sections as the Admin Panel's shipment list dialogs
 * (pages/B2BOrder/B2BOrderSummary.js): kind 'appointment' = "Appointment Delivery Details" (ABD, the current
 * appointment), kind 'additional' = "Additional Documents" (Non-ABD). No upload or edit here.
 */
export const VIEW_DOCS_TITLES = { appointment: 'Appointment Delivery Details', additional: 'Additional Documents' };

const Section = ({ title, children }) => (
    <Box>
        <Typography variant='caption' fontWeight={700} color='primary' sx={{ textTransform: 'uppercase', letterSpacing: 0.8 }}>{title}</Typography>
        <Divider sx={{ mt: 0.5, mb: 1.5 }} />
        {children}
    </Box>
);

const Item = ({ label, value, xs = 6 }) => (
    <Grid item xs={xs}>
        <Typography variant='caption' color='text.secondary'>{label}</Typography>
        <Typography variant='body2' fontWeight={500}>{value || '—'}</Typography>
    </Grid>
);

export default function ViewDocumentsDialog({ row, kind }) {
    const { enqueueSnackbar } = useSnackbar();
    const abd = kind === 'appointment';
    const d = (abd ? row?.appointmentDeliveryData : row?.additionalDocuments) || {};
    const docUrlApi = abd ? apiConstants.APPOINTMENT_DELIVERY_DOC_URL : apiConstants.ADDITIONAL_DOCUMENTS_DOC_URL;

    const open = async (params) => {
        try {
            const res = await getGeneric(docUrlApi, { awbNumber: row?.awb_number, ...params });
            const url = res?.data?.responseObj?.url || res?.data?.url;
            if (url) window.open(url, '_blank', 'noopener');
            else enqueueSnackbar('Document URL missing', { variant: 'warning' });
        } catch (err) {
            enqueueSnackbar(err?.response?.data?.message || 'Failed to fetch document', { variant: 'error' });
        }
    };

    const docs = (abd
        ? [
            { label: 'Invoice', key: d.appointment_del_invoice_s3_key, docType: 'INVOICE' },
            { label: 'E-Way Bill', key: d.appointment_del_eway_bill_s3_key, docType: 'EWAY_BILL' },
            { label: 'PO Copy', key: d.appointment_del_po_copy_s3_key, docType: 'PO_COPY' },
        ]
        : [
            { label: 'Invoice', key: d.invoiceS3Key, docType: 'INVOICE' },
            { label: 'E-Way Bill', key: d.ewayBillS3Key, docType: 'EWAY_BILL' },
            { label: 'PO Copy', key: d.poCopyS3Key, docType: 'PO_COPY' },
        ]).filter((doc) => doc.key);
    const extras = (abd ? d.appointment_del_extra_docs : d.extraDocs) || [];
    const alt1 = abd ? d.appointment_del_alt_contact_1 : d.altContact1;
    const alt2 = abd ? d.appointment_del_alt_contact_2 : d.altContact2;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
            {abd && (
                <Section title='Appointment Window'>
                    <Grid container spacing={2}>
                        <Item label='Start' value={parseDateAndTime(d.appointment_del_start)} />
                        <Item label='End' value={parseDateAndTime(d.appointment_del_end)} />
                    </Grid>
                </Section>
            )}
            <Section title='PO Details'>
                <Grid container spacing={2}>
                    <Item label='PO Number' value={abd ? d.appointment_del_po : d.poNumber} />
                    <Item label='PO Expiry' value={abd ? d.appointment_del_po_expiry : d.poExpiry} />
                    {abd && d.appointment_del_remarks && <Item label='Remarks' value={d.appointment_del_remarks} xs={12} />}
                </Grid>
            </Section>
            {(alt1 || alt2) && (
                <Section title='Alternate Contacts'>
                    <Grid container spacing={2}>
                        {alt1 && <Item label='Contact 1' value={alt1} />}
                        {alt2 && <Item label='Contact 2' value={alt2} />}
                    </Grid>
                </Section>
            )}
            {docs.length > 0 && (
                <Section title='Documents'>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {docs.map(({ label, docType }) => (
                            <Button key={docType} size='small' variant='contained' startIcon={<DocumentScanner />} onClick={() => open({ docType })}>{label}</Button>
                        ))}
                    </Box>
                </Section>
            )}
            {extras.length > 0 && (
                <Section title='Additional Documents'>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {extras.map((xd) => (
                            <Button key={xd.docId} size='small' variant='outlined' startIcon={<DocumentScanner />} onClick={() => open({ docId: xd.docId })}>
                                {xd.docLabel || categoryLabel(xd.docType)}
                            </Button>
                        ))}
                    </Box>
                </Section>
            )}
            {!docs.length && !extras.length && (
                <Typography variant='body2' color='text.secondary'>No documents uploaded yet.</Typography>
            )}
            {d.lastModifiedDate && (
                <Typography variant='caption' color='text.secondary'>Last modified on {formatDate(d.lastModifiedDate, 'dd MMM, yyyy HH:mm')}</Typography>
            )}
        </Box>
    );
}
